import { useMemo, useState } from "react";
import { Chip } from "../lux";
import { DateTimeField, VehicleCard, DriverCard, SearchBox, Field } from "./pickers";
import { Warning, Car } from "@phosphor-icons/react";

const RENTAL_DURATIONS = [
  { label: "1 day", h: 24 }, { label: "2 days", h: 48 }, { label: "3 days", h: 72 }, { label: "1 week", h: 168 },
];
const HOURLY_DURATIONS = [
  { label: "2 h", h: 2 }, { label: "3 h", h: 3 }, { label: "4 h", h: 4 }, { label: "6 h", h: 6 }, { label: "8 h", h: 8 },
];

const pad = (n) => String(n).padStart(2, "0");
const localISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Now, rounded UP to the next quarter hour. The duration chips have always
// rounded this way; SALES-012 gave it a name so the first date pick shares it.
const nextQuarterHour = () => {
  const d = new Date();
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
  return d;
};

const addHours = (iso, hours) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  d.setTime(d.getTime() + hours * 3600000);
  return localISO(d);
};

export default function StepSchedule({ formType, form, setForm, errors, ctx }) {
  const { vehicles, chauffeurs, bookings, bookedIds, checkingAvail, onVehicleChange, onDriverChange, moneyVisible } = ctx;
  const [vq, setVq] = useState("");
  const [showBooked, setShowBooked] = useState(false);
  const service = formType === "rental" ? "rental" : "chauffeur";

  const setStart = (val) => {
    setForm((f) => {
      // The date input emits `T00:00`, so the first pick on a blank form landed
      // on midnight and the time had to be typed. Snap it to now rounded up to
      // 15 — ONLY on the empty -> value transition, so a time entered
      // afterwards, 00:00 included, is the agent's and stands.
      let start = val;
      if (!f.start_datetime && val) {
        const n = nextQuarterHour();
        start = `${val.slice(0, 10)}T${pad(n.getHours())}:${pad(n.getMinutes())}`;
      }
      const next = { ...f, start_datetime: start };
      // End time follows the start: never before it, and pre-filled to the same
      // moment so the user only picks the return.
      if (!f.end_datetime || f.end_datetime < start) next.end_datetime = start;
      return next;
    });
  };

  const applyDuration = (hours) => {
    const start = form.start_datetime || localISO(nextQuarterHour());
    setForm((f) => ({ ...f, start_datetime: start, end_datetime: addHours(start, hours) }));
  };

  const pool = useMemo(() => vehicles
    .filter((v) => formType === "internal" ? true : (v.service_type === service || v.service_type === "both" || !v.service_type))
    .filter((v) => {
      const s = vq.trim().toLowerCase();
      if (!s) return true;
      return [v.make, v.model, v.year, v.license_plate, v.vehicle_class, v.website_name].some((x) => String(x || "").toLowerCase().includes(s));
    })
    .sort((a, b) => (a.make || "").localeCompare(b.make || "") || (a.model || "").localeCompare(b.model || "")),
    [vehicles, service, vq, formType]);

  const datesReady = Boolean(form.start_datetime && form.end_datetime && form.start_datetime < form.end_datetime);
  const isBooked = (id) => datesReady && bookedIds.includes(id);
  const available = pool.filter((v) => !isBooked(v.id));
  const booked = pool.filter((v) => isBooked(v.id));
  const selectedVehicle = vehicles.find((v) => v.id === form.vehicle_id);
  const selectedConflict = selectedVehicle && isBooked(selectedVehicle.id);

  // Driver conflicts are derived from the bookings this account can see.
  const driverBusy = useMemo(() => {
    if (!datesReady) return {};
    const s = form.start_datetime, e = form.end_datetime;
    const out = {};
    for (const b of bookings || []) {
      if (!b.driver_id || b.status === "cancelled" || b.status === "completed") continue;
      if (b.start_datetime && b.end_datetime && b.start_datetime < e && b.end_datetime > s) {
        out[b.driver_id] = b.customer_name || "another job";
      }
    }
    return out;
  }, [bookings, datesReady, form.start_datetime, form.end_datetime]);

  const durations = (formType === "rental" || formType === "internal") ? RENTAL_DURATIONS : HOURLY_DURATIONS;

  return (
    <div className="space-y-8">
      <div>
        <div className="label-mono mb-3">When</div>
        <div className="flex gap-2 flex-wrap mb-5" data-testid="wizard-duration-chips">
          {durations.map((d) => (
            <Chip key={d.label} onClick={() => applyDuration(d.h)} testid={`duration-${d.h}`}>{d.label}</Chip>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <DateTimeField
            label={formType === "rental" ? "Start of rental" : formType === "internal" ? "Start" : "Pickup time"}
            value={form.start_datetime}
            onChange={setStart}
            required
            error={errors.start_datetime}
            testid="bookings-field-start_datetime"
          />
          <DateTimeField
            label={formType === "rental" ? "End of rental" : formType === "internal" ? "End" : "Drop-off time"}
            value={form.end_datetime}
            onChange={(v) => setForm({ ...form, end_datetime: v })}
            min={form.start_datetime}
            required
            error={errors.end_datetime || (form.start_datetime && form.end_datetime && form.end_datetime < form.start_datetime ? "Must be after the start" : undefined)}
            hint="Must be after the start"
            testid="bookings-field-end_datetime"
          />
        </div>
      </div>

      <div>
        <div className="flex items-end justify-between gap-4 flex-wrap mb-3">
          <div>
            <div className="label-mono">Vehicle</div>
            <p className="text-xs text-muted-foreground mt-1">
              {!datesReady
                ? "Pick the dates first — we'll only show cars that are actually free."
                : checkingAvail
                  ? "Checking availability…"
                  : `${available.length} available for this window${booked.length ? ` · ${booked.length} already booked` : ""}`}
            </p>
          </div>
          <div className="w-full sm:w-72">
            <SearchBox value={vq} onChange={setVq} placeholder="Search make, model, plate…" testid="wizard-vehicle-search" />
          </div>
        </div>

        {errors.vehicle_id && (
          <p className="text-xs text-red-400 mb-3 flex items-center gap-1.5"><Warning size={12} />{errors.vehicle_id}</p>
        )}

        {selectedConflict && (
          <div className="mb-4 flex items-start gap-2.5 rounded-2xl border border-red-500/35 bg-red-500/[0.07] px-4 py-3 text-sm text-red-300" data-testid="vehicle-conflict-warning">
            <Warning size={16} className="mt-0.5 shrink-0" />
            <span>
              <b>{selectedVehicle.make} {selectedVehicle.model}</b> already has a confirmed booking overlapping this window.
              You can still continue — it will be flagged as a double-booking in the approval queue.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="wizard-vehicle-grid">
          {available.map((v) => (
            <VehicleCard key={v.id} v={v} selected={form.vehicle_id === v.id} onSelect={onVehicleChange} showOwner={moneyVisible} testid={`vehicle-option-${v.id}`} />
          ))}
        </div>
        {available.length === 0 && (
          <div className="border border-border bg-card px-5 py-10 text-center">
            <Car size={22} className="mx-auto text-muted-foreground/60 mb-3" />
            <div className="font-heading text-base">No cars free for this window</div>
            <p className="text-xs text-muted-foreground mt-1.5">Adjust the dates, or show the already-booked cars below.</p>
          </div>
        )}

        {booked.length > 0 && (
          <div className="mt-5">
            <Chip active={showBooked} onClick={() => setShowBooked((v) => !v)} testid="wizard-toggle-booked">
              {showBooked ? "Hide" : "Show"} already-booked cars ({booked.length})
            </Chip>
            {showBooked && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                {booked.map((v) => (
                  <VehicleCard key={v.id} v={v} selected={form.vehicle_id === v.id} booked onSelect={onVehicleChange} showOwner={moneyVisible} testid={`vehicle-option-${v.id}`} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {(formType === "chauffeur" || formType === "tour") && (
        <div>
          <div className="label-mono mb-1">Driver</div>
          <p className="text-xs text-muted-foreground mb-3">
            {datesReady ? "Availability shown for the selected window." : "Pick the dates to see who's free."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="wizard-driver-grid">
            {chauffeurs.map((d) => (
              <DriverCard
                key={d.id}
                d={d}
                selected={form.driver_id === d.id}
                busy={!!driverBusy[d.id]}
                busyLabel={driverBusy[d.id] ? `Already on ${driverBusy[d.id]}` : ""}
                onSelect={onDriverChange}
                testid={`driver-option-${d.id}`}
              />
            ))}
          </div>
          {chauffeurs.length === 0 && <Field label=""><span className="text-sm text-muted-foreground">No drivers on file.</span></Field>}
        </div>
      )}
    </div>
  );
}
